import { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableHead, TableRow, TableCell, TableBody,
  Switch, TextField, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  Select, MenuItem, FormControl, InputLabel, Chip, Alert, CircularProgress, IconButton, Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { api } from '../../api/client.js';
import type { ICategory } from '../../../shared/types.js';
import { CategoryScope, EngineMode } from '../../../shared/types.js';
import { colors } from '../../styles/theme.js';

type CategoryForm = {
  display_name: string;
  description: string;
  color: string;
  scope: CategoryScope;
};

type CreateFormState = CategoryForm & { internal_name: string };

const emptyCreateForm = (): CreateFormState => ({
  internal_name: '',
  display_name: '',
  description: '',
  color: '',
  scope: CategoryScope.DOMAIN_SCREENER,
});

function formFromCategory(cat: ICategory): CategoryForm {
  return {
    display_name: cat.display_name,
    description: cat.description ?? '',
    color: cat.color ?? '',
    scope: cat.scope,
  };
}

export default function CategoriesPage() {
  const [categories, setCategories] = useState<ICategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState<CreateFormState>(emptyCreateForm);
  const [editTarget, setEditTarget] = useState<ICategory | null>(null);
  const [editForm, setEditForm] = useState<CategoryForm>({
    display_name: '',
    description: '',
    color: '',
    scope: CategoryScope.DOMAIN_SCREENER,
  });
  const [deactivateTarget, setDeactivateTarget] = useState<ICategory | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ICategory | null>(null);

  const loadCategories = async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    const res = await api.get<{ categories: ICategory[] }>('/config/categories?engine_mode=legacy');
    if (res.success) setCategories(res.data.categories);
    else setError(res.error.message);
    setLoading(false);
  };

  useEffect(() => { loadCategories(); }, []);

  const openEdit = (cat: ICategory) => {
    setEditTarget(cat);
    setEditForm(formFromCategory(cat));
  };

  const handleCreate = async () => {
    const res = await api.post<{ category: ICategory }>('/config/categories', {
      internal_name: createForm.internal_name.trim(),
      display_name: createForm.display_name.trim(),
      description: createForm.description.trim() || null,
      color: createForm.color.trim() || null,
      scope: createForm.scope,
      engine_mode: EngineMode.LEGACY,
    });
    if (res.success) {
      setCreateOpen(false);
      setCreateForm(emptyCreateForm());
      loadCategories(true);
    } else {
      setError(res.error.message);
    }
  };

  const handleSaveEdit = async () => {
    if (!editTarget) return;
    const res = await api.patch(`/config/categories/${editTarget.id}`, {
      display_name: editForm.display_name.trim(),
      description: editForm.description.trim() || null,
      color: editForm.color.trim() || null,
      scope: editForm.scope,
    });
    if (res.success) {
      setEditTarget(null);
      loadCategories(true);
    } else {
      setError((res as { success: false; error: { message: string } }).error.message);
    }
  };

  const handleToggleActive = async (cat: ICategory) => {
    if (cat.active) {
      setDeactivateTarget(cat);
      return;
    }
    setCategories((prev) => prev.map((c) => c.id === cat.id ? { ...c, active: true } : c));
    const res = await api.patch(`/config/categories/${cat.id}`, { active: true });
    if (!res.success) {
      setError((res as { success: false; error: { message: string } }).error.message);
      loadCategories(true);
    }
  };

  const handleConfirmDeactivate = async () => {
    if (!deactivateTarget) return;
    const cat = deactivateTarget;
    setDeactivateTarget(null);
    setCategories((prev) => prev.map((c) => c.id === cat.id ? { ...c, active: false } : c));
    const res = await api.patch(`/config/categories/${cat.id}`, { active: false });
    if (!res.success) {
      setError((res as { success: false; error: { message: string } }).error.message);
      loadCategories(true);
    }
  };

  const handleConfirmDeleteCat = async () => {
    if (!deleteTarget) return;
    const cat = deleteTarget;
    setDeleteTarget(null);
    const res = await api.delete(`/config/categories/${cat.id}`);
    if (res.success) {
      loadCategories(true);
    } else {
      setError((res as { success: false; error: { message: string } }).error.message);
    }
  };

  const handleDuplicate = async (cat: ICategory) => {
    const res = await api.post(`/config/categories/${cat.id}/duplicate`, {});
    if (res.success) loadCategories(true);
    else setError((res as { success: false; error: { message: string } }).error.message);
  };

  const scopeLabel = (s: CategoryScope) =>
    s === CategoryScope.TRACKING_WATCHDOG ? 'Tracking Watchdog' : 'Domain Screener';

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Kategorien-Verwaltung</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setCreateForm(emptyCreateForm()); setCreateOpen(true); }}>
          Neue Kategorie
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Alle Kategorien aus der Datenbank. Deaktivieren statt löschen (No-Delete Policy).
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Paper elevation={0} sx={{ border: `1px solid ${colors.silver}` }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: colors.lightGrey }}>
              <TableCell>Internal Name</TableCell>
              <TableCell>Anzeigename</TableCell>
              <TableCell sx={{ minWidth: 220 }}>Beschreibung</TableCell>
              <TableCell>Farbe</TableCell>
              <TableCell>Scope</TableCell>
              <TableCell align="center">Aktiv</TableCell>
              <TableCell align="right" width={56} />
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={7} sx={{ textAlign: 'center', py: 6 }}>
                  <CircularProgress size={32} />
                </TableCell>
              </TableRow>
            )}
            {!loading && categories.map((cat) => (
              <TableRow key={cat.id} sx={{ opacity: cat.active ? 1 : 0.55 }}>
                <TableCell>
                  <Typography variant="body2" sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 13 }}>
                    {cat.internal_name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    ID: {cat.id}
                  </Typography>
                </TableCell>
                <TableCell>{cat.display_name}</TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                    {cat.description ?? '—'}
                  </Typography>
                </TableCell>
                <TableCell>
                  {cat.color ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 22,
                          height: 22,
                          borderRadius: 0.5,
                          bgcolor: cat.color,
                          border: `1px solid ${colors.silver}`,
                        }}
                      />
                      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{cat.color}</Typography>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">—</Typography>
                  )}
                </TableCell>
                <TableCell><Chip label={scopeLabel(cat.scope)} size="small" /></TableCell>
                <TableCell align="center">
                  <Switch checked={cat.active} onChange={() => handleToggleActive(cat)} />
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Bearbeiten">
                    <IconButton size="small" onClick={() => openEdit(cat)} aria-label="Kategorie bearbeiten">
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Duplizieren">
                    <IconButton size="small" onClick={() => handleDuplicate(cat)}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Löschen">
                    <IconButton size="small" onClick={() => setDeleteTarget(cat)}>
                      <DeleteOutlineIcon fontSize="small" sx={{ color: colors.scoreRed }} />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {!loading && categories.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>Keine Kategorien vorhanden</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Neue Kategorie</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          <TextField
            required
            label="Internal Name"
            value={createForm.internal_name}
            onChange={(e) => setCreateForm({ ...createForm, internal_name: e.target.value.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '') })}
            helperText="Eindeutig, z. B. consent_config (nach Anlage nicht änderbar)"
          />
          <TextField
            required
            label="Anzeigename"
            value={createForm.display_name}
            onChange={(e) => setCreateForm({ ...createForm, display_name: e.target.value })}
          />
          <TextField
            label="Beschreibung"
            multiline
            minRows={2}
            value={createForm.description}
            onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
          />
          <TextField
            label="Farbe (Hex)"
            placeholder="#1F4E79"
            value={createForm.color}
            onChange={(e) => setCreateForm({ ...createForm, color: e.target.value })}
            InputProps={{
              startAdornment: createForm.color.match(/^#[0-9A-Fa-f]{6}$/) ? (
                <Box sx={{ width: 20, height: 20, borderRadius: 0.5, bgcolor: createForm.color, mr: 1, border: `1px solid ${colors.silver}` }} />
              ) : undefined,
            }}
          />
          <FormControl>
            <InputLabel>Scope</InputLabel>
            <Select
              value={createForm.scope}
              label="Scope"
              onChange={(e) => setCreateForm({ ...createForm, scope: e.target.value as CategoryScope })}
            >
              <MenuItem value={CategoryScope.DOMAIN_SCREENER}>Domain Screener</MenuItem>
              <MenuItem value={CategoryScope.TRACKING_WATCHDOG}>Tracking Watchdog</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Abbrechen</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={!createForm.internal_name.trim() || !createForm.display_name.trim()}
          >
            Erstellen
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!editTarget} onClose={() => setEditTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Kategorie bearbeiten</DialogTitle>
        {editTarget && (
          <>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
              <Typography variant="caption" color="text.secondary">
                Internal Name (fest):{' '}
                <Box component="span" sx={{ fontFamily: 'monospace' }}>{editTarget.internal_name}</Box>
              </Typography>
              <TextField
                label="Anzeigename"
                value={editForm.display_name}
                onChange={(e) => setEditForm({ ...editForm, display_name: e.target.value })}
              />
              <TextField
                label="Beschreibung"
                multiline
                minRows={3}
                value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
              />
              <TextField
                label="Farbe (Hex)"
                placeholder="#1F4E79"
                value={editForm.color}
                onChange={(e) => setEditForm({ ...editForm, color: e.target.value })}
                InputProps={{
                  startAdornment: editForm.color.match(/^#[0-9A-Fa-f]{6}$/) ? (
                    <Box sx={{ width: 20, height: 20, borderRadius: 0.5, bgcolor: editForm.color, mr: 1, border: `1px solid ${colors.silver}` }} />
                  ) : undefined,
                }}
              />
              <FormControl>
                <InputLabel>Scope</InputLabel>
                <Select
                  value={editForm.scope}
                  label="Scope"
                  onChange={(e) => setEditForm({ ...editForm, scope: e.target.value as CategoryScope })}
                >
                  <MenuItem value={CategoryScope.DOMAIN_SCREENER}>Domain Screener</MenuItem>
                  <MenuItem value={CategoryScope.TRACKING_WATCHDOG}>Tracking Watchdog</MenuItem>
                </Select>
              </FormControl>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setEditTarget(null)}>Abbrechen</Button>
              <Button variant="contained" onClick={handleSaveEdit}>Speichern</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Dialog open={!!deactivateTarget} onClose={() => setDeactivateTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Kategorie deaktivieren?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Möchtest du <strong>{deactivateTarget?.display_name}</strong> wirklich deaktivieren?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeactivateTarget(null)}>Abbrechen</Button>
          <Button variant="contained" color="error" onClick={handleConfirmDeactivate}>Deaktivieren</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Kategorie löschen?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Möchtest du <strong>{deleteTarget?.display_name}</strong> wirklich endgültig löschen? Diese Aktion kann nicht rückgängig gemacht werden.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Abbrechen</Button>
          <Button variant="contained" color="error" onClick={handleConfirmDeleteCat}>Endgültig löschen</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
